// Cookie consent banner (optional/analytics cookies).
//
// The markup lives in templates/_layouts/_partials/cookie-consent.twig and is
// only rendered on the main public site, so this is a no-op everywhere else.
//
// State is stored in a plain first-party cookie `cookie_consent=accepted|rejected`
// (read client-side; we deliberately don't use Craft's validated cookies so the
// value can be set from JS). Choosing "Accept" reloads so the now-unblocked
// trackers in _partials/analytics.twig initialise.
import { claimBottomSpace } from './bottom-space.js';
import { onReady } from './dom.js';
import { beacon } from './analytics-beacons.js';

// Counting the choice, including the people who say no (#422).
//
// Matomo is consent-gated on this site, so it structurally cannot count the visitors it
// is not counting -- a rejection means the tracker never loads. That makes the accept
// rate the denominator for every Matomo figure we have, and the one number Matomo can
// never supply.
//
// This beacon may run before consent for the same reason the other first-party ones do:
// it sets no cookie and stores nothing about anybody. What reaches the database is one
// increment on a per-day counter. Counting a rejection in any way that could be traced
// back to the person rejecting would be a poor way to respect the rejection.
var CONSENT_ENDPOINT = '/actions/_event-analytics/reporting/record-consent';

// Once per browsing session, not once per pageview.
//
// Without this the `stored` figure would count pageviews rather than visits, so a reader
// of ten pages would outweigh ten visitors reading one -- and the accept rate would
// silently become a measure of how much people browse. sessionStorage is the right
// granularity and, unlike a cookie, adds nothing to the request.
var SESSION_FLAG = 'ea_consent_counted';

// The banner appearing unprompted, counted once per session under its own flag (#442).
//
// Without it a visitor who ignores the banner and leaves is invisible here and to Matomo
// alike, so visit coverage could only ever be a ceiling. Separate from SESSION_FLAG
// because the two are not exclusive: the banner appears, and then a decision follows
// in the same session and must still be counted.
var SHOWN_FLAG = 'ea_consent_shown';

function alreadyCountedThisSession(flag) {
    try {
        return window.sessionStorage.getItem(flag || SESSION_FLAG) === '1';
    } catch (e) {
        // Private mode and blocked storage both throw. Treat it as counted: over-counting
        // a repeat visitor skews the rate, under-counting them loses one data point.
        // For `shown` the report floors undecided at zero, so a decision arriving
        // without its `shown` cannot push coverage above the ceiling.
        return true;
    }
}

function reportConsent(outcome, source) {
    // No token means this is not the main site, or the page predates the change. Either
    // way there is nothing to report to.
    if (!window.__eaConsent || !window.__eaConsent.token) {
        return;
    }

    // Set the flag BEFORE sending, and on the banner path too. Accepting reloads the
    // page, and the reload would otherwise see a stored decision and count the same
    // visitor a second time.
    try {
        window.sessionStorage.setItem(outcome === 'shown' ? SHOWN_FLAG : SESSION_FLAG, '1');
    } catch (e) { /* nothing to do; the beacon is still worth sending */ }

    beacon(CONSENT_ENDPOINT, { token: window.__eaConsent.token, outcome: outcome, source: source });
}

var COOKIE = 'cookie_consent';

function getConsent() {
    var m = document.cookie.match(/(?:^|;\s*)cookie_consent=(accepted|rejected)(?:;|$)/);
    return m ? m[1] : null;
}

function setConsent(v) {
    // ~13 months, first-party, Lax.
    // Secure: the site is HTTPS-only, so there is no reason to let this travel over
    // plain HTTP. It holds 'accepted' or 'rejected' and nothing else, but that is an
    // argument for the flag being free rather than for leaving it off.
    document.cookie = COOKIE + '=' + v + ';path=/;max-age=' + (60 * 60 * 24 * 400) + ';SameSite=Lax;Secure';
}

// Keep the page clear of the banner.
//
// The banner is fixed to the bottom of the viewport, so it sits ON TOP of whatever is
// there -- which on most pages is the footer. Anyone tabbing into a footer link had it
// focused underneath the banner and invisible (WCAG 2.4.11 Focus Not Obscured), and the
// links ended up within a few pixels of the banner's own buttons, which is a 2.5.8 Target
// Size failure too. The accessibility suite found 75 findings across those two rules, all
// from this one overlap.
//
// Reserving the space rather than making the banner sticky keeps it visible on arrival,
// which is the reason it is fixed in the first place. The height is measured rather than
// assumed because the text wraps at narrow widths, so the banner is two or three lines
// tall on a phone and one on a desktop.
function reserveSpaceFor(banner) {
    var apply = function () {
        var visible = !banner.classList.contains('hidden');

        // padding-bottom and scroll-padding-bottom, shared with the feedback box: see
        // bottom-space.js for why both properties, and why through a shared claim.
        claimBottomSpace('cookie-banner', visible ? banner.offsetHeight : 0);
    };

    apply();

    // Re-measure when the banner rewraps. ResizeObserver catches both the viewport
    // changing and the text reflowing; the resize listener is the fallback for browsers
    // below our floor that lack it.
    if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(apply).observe(banner);
    } else {
        window.addEventListener('resize', apply);
    }

    return apply;
}

onReady(function () {
    var banner = document.getElementById('cookie-consent');
    if (!banner) return;

    // Show the banner only until a choice is made.
    var stored = getConsent();
    if (!stored) {
        banner.classList.remove('hidden');
        // Only here, where it appears unprompted. Reopening it from the footer is a
        // visitor with a decision already counted, not a new visit to the question.
        if (!alreadyCountedThisSession(SHOWN_FLAG)) {
            reportConsent('shown', 'banner');
        }
    } else if (!alreadyCountedThisSession()) {
        // A decision made on an earlier visit, being honoured rather than asked again.
        // Counted apart from `banner` because it answers a different question: what the
        // standing split is among everyone arriving, rather than what people choose when
        // first asked.
        reportConsent(stored, 'stored');
    }

    var releaseSpace = reserveSpaceFor(banner);

    // Let people change their mind.
    //
    // Until this existed the banner appeared once and never again: it renders only when
    // no `cookie_consent` cookie is set, and nothing anywhere re-opened it. Someone who
    // accepted and then thought better of it had no route back except clearing their
    // browser data for the site. The ICO's position is that withdrawing consent should be
    // as straightforward as giving it, so "clear your cookies" is not an answer (#306).
    //
    // Any element can be the control -- the footer has one, and the cookie policy page
    // can carry one in its content -- so this is delegated from the document rather than
    // bound to a known id.
    document.addEventListener('click', function (e) {
        var opener = e.target.closest('[data-consent-reopen]');
        if (!opener) return;
        e.preventDefault();
        banner.classList.remove('hidden');
        releaseSpace = reserveSpaceFor(banner);
        // Focus the banner so a keyboard user is taken to the thing they just asked for,
        // rather than left at the bottom of the page wondering whether it worked.
        banner.setAttribute('tabindex', '-1');
        banner.focus();
    });

    banner.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-consent]');
        if (!btn) return;
        var choice = btn.getAttribute('data-consent');
        setConsent(choice);
        reportConsent(choice, 'banner');
        banner.classList.add('hidden');
        releaseSpace(); // Give the page its space back.
        // On accept, reload so the consent-gated trackers initialise. On reject,
        // nothing to load — just dismiss.
        if (choice === 'accepted') {
            window.location.reload();
        }
    });
});
